import JWPlayer from '@jwplayer/jwplayer-react';
import { skipToken } from '@reduxjs/toolkit/query';
import Cookies from 'js-cookie';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { CleTimeRecord } from '../datasource/generated';
import { useGetCleTimeRecordsQuery } from '../datasource/queries/cle';
import { useLazyGetTrackCleQuery } from '../datasource/queries/trackCle';
import { CleTimerEvent, setTimeStampEvent } from '../stories/Store/cleSlice';
import { ExternalCommand, setExternalCommand } from '../stories/Store/playerSlice';
import logger from '../utility/logger';
import { WorkerInterval } from '../utility/WorkerInterval';
import { useAppDispatch, useAppSelector, useAppStore } from '.';
import { MediaLogEventTypes, useLogMediaEvents } from './useLogMediaEvents';

interface CleTimer {
    weightSeconds: number,
    accruedSeconds: number
}

const cookieOptions = {
    sameSite: 'strict',
    expires: 30,
};

type TCurrentTimer = CleTimer | null;

interface Props {
    playerRef: MutableRefObject<typeof JWPlayer>
}

type TrackCleQuery = (itemSk: number) => PromiseLike<{ data?: boolean }>;

export const getTrackCleForSegment = async (
    itemSk: number,
    getTrackCle: TrackCleQuery,
    handleError: (error: Error) => void,
) => {
    try {
        const response = await getTrackCle(itemSk);
        return response.data ?? true;
    } catch (error) {
        handleError(error as Error);
        return true;
    }
};

export const getTrackCleFromTimeRecords = (
    itemSk: number,
    timeRecords: CleTimeRecord[] | undefined,
    handleError: (error: Error) => void,
) => {
    const timeRecord = timeRecords?.find(record => !record.isLive && record.item_SK === itemSk);

    if (typeof timeRecord?.trackCle === 'boolean')
        return timeRecord.trackCle;

    handleError(new Error(`Unable to resolve an on-demand CLE time record for item ${itemSk}`));
    return true;
};

export const useCleTimer = ({ playerRef }: Props) => {

    const dispatch = useAppDispatch();
    const store = useAppStore();

    const { logMediaEvent } = useLogMediaEvents();

    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const {
        data: cleTimeRecords,
        error: cleTimeRecordsError,
        isError: isCleTimeRecordsError,
    } = useGetCleTimeRecordsQuery(
        !launchDetails?.isWebcast && launchDetails?.encryptedRegistrationId
            ? launchDetails.encryptedRegistrationId
            : skipToken
    );
    const cookieName = `cle-${launchDetails?.itemSk}`;
    const [isCleTimerRunning, setIsCleTimerRunning] = useState(false);
    const currentTimer = useRef<TCurrentTimer>(null);
    const setCurrentTimer = useCallback((value: TCurrentTimer | ((old: TCurrentTimer) => TCurrentTimer)) => {
        const newValue = typeof value === 'function'
            ? value(currentTimer.current)
            : value;
        currentTimer.current = newValue;
        Cookies.set(
            cookieName,
            JSON.stringify(newValue),
            cookieOptions
        );
    }, [cookieName]);

    // For Live this is the Live Webcast itemSk, for OD it is the current segment itemSk
    const itemSk = useAppSelector((state) => state.player.currentSegmentItemSk);
    const individualSk = useAppSelector((state) => state.user.individualSK);
    const sequence = useRef(1);
    const currentSegmentItemSkRef = useRef(itemSk);
    const activeCleDialogEventRef = useRef<CleTimerEvent | null>(null);

    useEffect(() => {
        currentSegmentItemSkRef.current = itemSk;
    }, [itemSk]);

    useEffect(() => {
        const updateActiveCleDialogEvent = () => {
            activeCleDialogEventRef.current = store.getState().cle.timeStampEvent;
        };

        updateActiveCleDialogEvent();
        return store.subscribe(updateActiveCleDialogEvent);
    }, [store]);

    const getRandomWeightSeconds = useCallback(() => {
        const minMinutes = launchDetails?.minWeight ?? 8;
        const maxMinutes = launchDetails?.maxWeight ?? 12;

        const randomMinutes = Math.floor(Math.random() * (maxMinutes - minMinutes + 1)) + minMinutes;

        return randomMinutes * 60;

    }, [launchDetails]);

    const getNewCleTimer = useCallback(() => {
        const weight = getRandomWeightSeconds();
        return {
            weightSeconds: weight,
            accruedSeconds: 0,
        };
    }, [getRandomWeightSeconds]);

    const cleIntervalLastFiredTime = useRef<number>();

    const getCleIntervalElapsedSeconds = useCallback((timestamp: number) => {
        if (!cleIntervalLastFiredTime.current)
            return 0;
        const ellapsedMillis = timestamp - cleIntervalLastFiredTime.current;
        const elapsedSeconds = ellapsedMillis / 1000;
        return parseFloat(elapsedSeconds.toFixed(3));
    }, []);

    const startCleTimer = useCallback(() => {
        if (isCleTimerRunning) return;

        setIsCleTimerRunning(true);
        if (!currentTimer.current) {
            const newTimer = getNewCleTimer();
            setCurrentTimer(newTimer);
            logger.log('Start CLE timer, creating new timer', {
                individualSk: individualSk,
                itemSk: launchDetails.itemSk,
                currentSegmentItemSk: itemSk,
                timer: newTimer,
                eventTime: Date(),
            });
        } else {
            logger.log('Start CLE timer, resuming existing timer', {
                individualSk: individualSk,
                itemSk: launchDetails.itemSk,
                currentSegmentItemSk: itemSk,
                timer: currentTimer.current,
                eventTime: Date(),
            });
        }
        cleIntervalLastFiredTime.current = performance.now();
    }, [getNewCleTimer, individualSk, isCleTimerRunning, itemSk, launchDetails.itemSk, setCurrentTimer]);

    const pauseCleTimer = useCallback(() => {
        logger.log('Pause CLE timer', {
            individualSk: individualSk,
            itemSk: launchDetails.itemSk,
            currentSegmentItemSk: itemSk,
            timer: currentTimer.current,
            eventTime: Date(),
        });
        setIsCleTimerRunning(false);
        cleIntervalLastFiredTime.current = undefined;
    }, [individualSk, itemSk, launchDetails.itemSk]);

    useEffect(() => {
        // Try to retrieve the timer from the cookie when the component initially mounts
        const cookieValue = Cookies.get(cookieName);
        if (cookieValue) {
            const timer = JSON.parse(cookieValue);
            logger.log('Retrieved CLE timer from cookie', {
                individualSk: individualSk,
                itemSk: itemSk,
                timer: timer,
                cookieValue: cookieValue,
                eventTime: Date(),
            });
            setCurrentTimer(timer);
        }
    }, [cookieName, individualSk, itemSk, setCurrentTimer]);

    const [getTrackCle] = useLazyGetTrackCleQuery();

    useEffect(() => {
        if (!isCleTimeRecordsError) return;

        logger.error('Error trying to load CLE time records', { error: cleTimeRecordsError });
        logMediaEvent(MediaLogEventTypes.ERROR, 'Unable to load CLE time records for on-demand attendance verification');
    }, [cleTimeRecordsError, isCleTimeRecordsError, logMediaEvent]);

    useEffect(() => {
        if (!isCleTimerRunning) return;

        const timerInterval = new WorkerInterval(async (timestamp) => {
            const currentSequence = sequence.current;
            const currentTimerValue = currentTimer.current;
            const elapsedSeconds = getCleIntervalElapsedSeconds(timestamp);

            const getNewTimerIfWeightReached = () => {
                if (!currentTimerValue)
                    return getNewCleTimer();

                if (currentTimerValue.accruedSeconds + elapsedSeconds < currentTimerValue.weightSeconds) {
                    return null;
                }

                const bleedingSeconds = parseFloat(((currentTimerValue.accruedSeconds + elapsedSeconds) - currentTimerValue.weightSeconds).toFixed(3));

                logger.log('CLE Timer weight reached', {
                    individualSk: individualSk,
                    itemSk: launchDetails.itemSk,
                    currentSegmentItemSk: itemSk,
                    eventTime: Date(),
                    weightSeconds: currentTimerValue.weightSeconds,
                });

                const handleTrackCleError = (error: Error) => {
                    logger.error('Error trying to check track cle', { error });
                    logMediaEvent(MediaLogEventTypes.ERROR, `getTrackCleForSegment(): ${error.message}`);
                };
                const trackClePromise = launchDetails.isWebcast
                    ? getTrackCleForSegment(itemSk, getTrackCle, handleTrackCleError)
                    : Promise.resolve(getTrackCleFromTimeRecords(itemSk, cleTimeRecords, handleTrackCleError));
                // If either eligibility source fails, default to true to avoid missed attendance.

                trackClePromise.then(trackCle => {
                    if (currentSegmentItemSkRef.current !== itemSk) return;

                    if (!trackCle) {
                        logger.log('CLE Timer is not tracking', {
                            individualSk: individualSk,
                            itemSk: launchDetails.itemSk,
                            currentSegmentItemSk: itemSk,
                            eventTime: Date(),
                            sequence: sequence,
                            trackCle,
                        });
                        return;
                    }

                    const activeCleDialogEvent = activeCleDialogEventRef.current;

                    // If previous dialog hasn't been dismissed, don't send a new event
                    if (!activeCleDialogEvent) {
                        const event: CleTimerEvent = {
                            registrationId: launchDetails.encryptedRegistrationId ?? '',
                            itemSk: itemSk,
                            weight: Math.round(currentTimerValue.weightSeconds / 60),
                            indexGuid: uuidv4(),
                            displayDateSeconds: Math.round(new Date().getTime() / 1000),
                            sequence: currentSequence,
                            minWeight: launchDetails.minWeight ?? 8,
                            maxWeight: launchDetails.maxWeight ?? 12,
                        };
        
                        logger.log('CLE Timer weight reached, showing attendance dialog', {
                            individualSk: individualSk,
                            itemSk: launchDetails.itemSk,
                            currentSegmentItemSk: itemSk,
                            eventTime: Date(),
                            event: event,
                            sequence: sequence,
                        });
        
                        dispatch(setTimeStampEvent(event));
                        sequence.current += 1;
                    } else if (activeCleDialogEvent && !launchDetails?.isWebcast) {

                        logger.log('CLE Timer weight reached but user is inactive, pause timer and video', {
                            individualSk: individualSk,
                            itemSk: launchDetails.itemSk,
                            currentSegmentItemSk: itemSk,
                            eventTime: Date(),
                        });
        
                        dispatch(setExternalCommand(ExternalCommand.pause));
                    }
                });
                // Reset accrued time to 0 and get new weight
                const newTimer = getNewCleTimer();

                // bleed exceeding time into next cle timer
                newTimer.accruedSeconds += bleedingSeconds;

                return newTimer;
            };

            const newTimer = getNewTimerIfWeightReached();

            setCurrentTimer((prevTimer) => {
                cleIntervalLastFiredTime.current = timestamp;
                if (newTimer) {
                    logger.log('Creating new CLE Timer', {
                        individualSk: individualSk,
                        itemSk: launchDetails.itemSk,
                        currentSegmentItemSk: itemSk,
                        timer: newTimer,
                        eventTime: Date(),
                    });
                    return newTimer;
                }
                else if (prevTimer) {
                    const updatedTimer : CleTimer = {
                        ...prevTimer,
                        accruedSeconds: Math.max(
                            0,
                            prevTimer.accruedSeconds,
                            Number((prevTimer.accruedSeconds + elapsedSeconds).toFixed(3)),
                        ),
                    };
        
                    if (Math.round(updatedTimer.accruedSeconds) % 60 === 0) {
                        logger.log('Accrued minute on CLE Timer', {
                            individualSk: individualSk,
                            itemSk: launchDetails.itemSk,
                            currentSegmentItemSk: itemSk,
                            timer: updatedTimer,
                            eventTime: Date(),
                        });
                    }
    
                    return updatedTimer;
                } else {
                    return null;
                }
            });
        }, 1000);

        const abort = new AbortController();

        let frozenTab = false;
        window.addEventListener('freeze', () => {
            // only non-async calls are available here (so we can't safely rely on redux or react lifecycle)
            if (launchDetails?.isWebcast) {
                logger.log('[Webcast Only] Reloading the page due to tab freeze, page will load again after tab is restored');
                return location.reload();
            }
            logger.log('[On-Demand only] Pausing Video & CLE due to tab freeze');
            frozenTab = true;
            timerInterval.stop();
            playerRef.current?.pause();
        }, { signal: abort.signal, capture: true });

        ['resume', 'visibilitychange'].forEach(event =>
            window.addEventListener(event, () => {
                if (!frozenTab) return;
                logger.log('Restoring frozen tab');
                // since the video is now paused, we turn the timer controller off manually
                pauseCleTimer();
            }, { signal: abort.signal, capture: true })
        );

        return () => {
            abort.abort();
            timerInterval.stop();
        };
    }, [
        cleTimeRecords, dispatch, getCleIntervalElapsedSeconds, getNewCleTimer, getRandomWeightSeconds, getTrackCle, individualSk,
        isCleTimerRunning, itemSk, launchDetails.encryptedRegistrationId, launchDetails.isWebcast,
        launchDetails.itemSk, launchDetails.maxWeight, launchDetails.minWeight, setCurrentTimer,
        logMediaEvent, pauseCleTimer, startCleTimer, playerRef,
    ]);

    return {
        isCleTimerRunning,
        startCleTimer,
        pauseCleTimer,
    };
};

export default useCleTimer;
